/**
 * CRM Pipeline Board
 *
 * Main Kanban board component with DndContext
 */

import { useCallback, useEffect, useState } from 'react';
import { DndContext, DragOverlay, closestCorners } from '@dnd-kit/core';
import { useCrmDragDrop } from '../../hooks';
import { CrmColumn } from './CrmColumn';
import { CrmOpportunityCard } from './CrmOpportunityCard';
import type { PipelineBoard, PipelineOpportunity } from '../../types';

const COLLAPSED_LANES_KEY = 'atek-crm-pipeline-collapsed-lanes';

interface CrmPipelineBoardProps {
  board: PipelineBoard;
  onViewOpportunity?: (opportunity: PipelineOpportunity) => void;
  onAddActivity?: (opportunity: PipelineOpportunity) => void;
  onMarkLost?: (opportunity: PipelineOpportunity) => void;
  onQuoteTransform?: (opportunity: PipelineOpportunity) => void;
  onLoadMore?: (stage: string) => void;
  loadingStage?: string | null;
}

export function CrmPipelineBoard({
  board,
  onViewOpportunity,
  onAddActivity,
  onMarkLost,
  onQuoteTransform,
  onLoadMore,
  loadingStage,
}: CrmPipelineBoardProps) {
  const { sensors, activeOpportunity, isMoving, handleDragStart, handleDragEnd } = useCrmDragDrop({
    columns: board.columns,
    onQuoteTransform,
  });

  const [collapsedLanes, setCollapsedLanes] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(COLLAPSED_LANES_KEY);
      return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_LANES_KEY, JSON.stringify([...collapsedLanes]));
    } catch { /* ignore */ }
  }, [collapsedLanes]);

  const toggleLane = useCallback((key: string) => {
    setCollapsedLanes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex-1 overflow-x-auto p-6 relative">
        {/* Loading overlay during move operation */}
        {isMoving && (
          <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center">
            <div className="text-sm text-gray-500">Updating...</div>
          </div>
        )}

        <div className="flex gap-4">
          {board.columns.map((column) => (
            <CrmColumn
              key={column.key}
              column={column}
              collapsed={collapsedLanes.has(column.key)}
              onToggleCollapsed={() => toggleLane(column.key)}
              onViewOpportunity={onViewOpportunity}
              onAddActivity={onAddActivity}
              onCreateQuote={onQuoteTransform}
              onMarkLost={onMarkLost}
              onLoadMore={onLoadMore ? () => onLoadMore(column.key) : undefined}
              isLoadingMore={loadingStage === column.key}
            />
          ))}
        </div>
      </div>

      {/* Drag Overlay - renders the card being dragged */}
      <DragOverlay>
        {activeOpportunity ? (
          <CrmOpportunityCard opportunity={activeOpportunity} isDragging />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
